import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminBetaEventCodeRequestsPanel from '../components/admin/AdminBetaEventCodeRequestsPanel';
import { PLATFORM_ADMIN_EMAIL } from '../constants/platformAdmin';
import { getSession } from '../services/authService';
import {
  clearBetaTesterFeedbackCategory,
  listBetaTesterFeedback,
  type BetaTesterFeedbackCategoryField,
} from '../services/platformBetaTesterFeedbackService';
import type { BetaTesterFeedbackRow } from '../types/betaTesterFeedback';

function isAdminSessionEmail(email: string): boolean {
  return email.trim().toLowerCase() === PLATFORM_ADMIN_EMAIL;
}

function formatAt(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const PAGE_SIZE = 20;

type FeedbackCategoryKey = BetaTesterFeedbackCategoryField;

const FEEDBACK_CATEGORIES: {
  key: FeedbackCategoryKey;
  label: string;
  pick: (row: BetaTesterFeedbackRow) => string;
}[] = [
  { key: 'goodPoints', label: '좋은점', pick: (r) => r.goodPoints },
  { key: 'useCases', label: '활용방안', pick: (r) => r.useCases },
  { key: 'improvements', label: '개선하면 좋은 점', pick: (r) => r.improvements },
  { key: 'bugs', label: '버그', pick: (r) => r.bugs },
];

const FEEDBACK_CATEGORY_LABEL: Record<FeedbackCategoryKey, string> = {
  goodPoints: '좋은점',
  useCases: '활용방안',
  improvements: '개선하면 좋은 점',
  bugs: '버그',
};

type FeedbackDeleteTarget = {
  id: string;
  category: FeedbackCategoryKey;
  fullName: string;
  email: string;
};

const EMPTY_FEEDBACK_PAGES: Record<FeedbackCategoryKey, number> = {
  goodPoints: 0,
  useCases: 0,
  improvements: 0,
  bugs: 0,
};

const AdminBetaTesterPage: React.FC = () => {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [feedback, setFeedback] = useState<BetaTesterFeedbackRow[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackPages, setFeedbackPages] =
    useState<Record<FeedbackCategoryKey, number>>(EMPTY_FEEDBACK_PAGES);
  const [deleteTarget, setDeleteTarget] = useState<FeedbackDeleteTarget | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const session = await getSession();
        const email = session?.user?.email ?? '';
        if (cancelled) return;
        setIsAdmin(isAdminSessionEmail(email));
      } catch {
        if (!cancelled) setIsAdmin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const reloadFeedback = useCallback(async () => {
    try {
      setFeedbackLoading(true);
      setFeedbackError(null);
      const rows = await listBetaTesterFeedback();
      setFeedback(rows);
    } catch (err) {
      console.error(err);
      setFeedbackError(
        '베타 테스터 의견 목록을 불러오지 못했습니다. 마이그레이션(platform_beta_tester_feedback)을 확인해 주세요.',
      );
    } finally {
      setFeedbackLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) void reloadFeedback();
  }, [isAdmin, reloadFeedback]);

  const byCategory = useMemo(() => {
    return FEEDBACK_CATEGORIES.map((cat) => {
      const items = feedback
        .map((row) => ({ row, text: cat.pick(row).trim() }))
        .filter((x) => x.text.length > 0);
      const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
      const page = Math.min(feedbackPages[cat.key] ?? 0, pageCount - 1);
      const start = page * PAGE_SIZE;
      return {
        ...cat,
        items,
        page,
        pageCount,
        pageItems: items.slice(start, start + PAGE_SIZE),
      };
    });
  }, [feedback, feedbackPages]);

  const setCategoryPage = (key: FeedbackCategoryKey, next: number) => {
    setFeedbackPages((prev) => ({ ...prev, [key]: Math.max(0, next) }));
  };

  const closeDeleteModal = () => {
    if (deleteBusy) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const confirmDeleteFeedback = async () => {
    if (!deleteTarget || deleteBusy) return;
    try {
      setDeleteBusy(true);
      setDeleteError(null);
      const outcome = await clearBetaTesterFeedbackCategory(
        deleteTarget.id,
        deleteTarget.category,
      );
      if (outcome.result === 'deleted') {
        setFeedback((rows) => rows.filter((r) => r.id !== deleteTarget.id));
      } else {
        setFeedback((rows) =>
          rows.map((r) => (r.id === deleteTarget.id ? outcome.row : r)),
        );
      }
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      setDeleteError('의견 삭제에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setDeleteBusy(false);
    }
  };

  if (isAdmin === null) {
    return (
      <section className="cp-admin-notices-page cp-admin-notices-page--edu" aria-busy="true">
        <p className="cp-dashboard-placeholder" role="status">
          로딩 중…
        </p>
      </section>
    );
  }

  if (!isAdmin) {
    return (
      <section className="cp-admin-notices-page cp-admin-notices-page--edu">
        <p className="cp-dashboard-placeholder" role="alert">
          이 메뉴는 관리자만 이용할 수 있습니다.
        </p>
      </section>
    );
  }

  return (
    <section
      className="cp-admin-notices-page cp-admin-notices-page--edu cp-admin-beta-tester-page"
      aria-labelledby="cp-admin-beta-tester-title"
    >
      <div className="cp-page-title-row cp-admin-notices-page-title-row">
        <div className="cp-admin-legal-title-stack">
          <h2 id="cp-admin-beta-tester-title">베타 테스트</h2>
          <p className="cp-admin-legal-subtitle">
            테스터가 남긴 의견과 이벤트 코드 요청을 한곳에서 확인·처리합니다.
          </p>
        </div>
      </div>

      {feedbackError ? (
        <p className="cp-dashboard-placeholder" role="alert">
          {feedbackError}
        </p>
      ) : null}

      <div className="cp-admin-beta-feedback-grid" role="group" aria-label="베타 테스터 의견">
        {byCategory.map((cat) => {
          const headId = `cp-admin-beta-feedback-head-${cat.key}`;
          return (
            <article
              key={cat.key}
              className="cp-dashboard-block cp-admin-users-pass-block cp-admin-beta-feedback-col"
              aria-labelledby={headId}
            >
              <h3 className="cp-dashboard-block__head" id={headId}>
                <span className="cp-admin-beta-head-pager" role="group" aria-label={`${cat.label} 페이지`}>
                  <button
                    type="button"
                    className="cp-admin-beta-head-pager-btn"
                    aria-label="이전 페이지"
                    disabled={cat.page <= 0}
                    onClick={() => setCategoryPage(cat.key, cat.page - 1)}
                  >
                    &lt;
                  </button>
                  <button
                    type="button"
                    className="cp-admin-beta-head-pager-btn"
                    aria-label="다음 페이지"
                    disabled={cat.page >= cat.pageCount - 1 || cat.items.length === 0}
                    onClick={() => setCategoryPage(cat.key, cat.page + 1)}
                  >
                    &gt;
                  </button>
                </span>
                <span className="cp-dashboard-block__head-title">
                  {cat.label}
                  <span className="cp-admin-beta-feedback-count"> ({cat.items.length}건)</span>
                </span>
              </h3>
              <div className="cp-admin-beta-feedback-col-body">
                {feedbackLoading ? (
                  <p className="cp-dashboard-placeholder" role="status">
                    불러오는 중…
                  </p>
                ) : cat.items.length === 0 ? (
                  <p className="cp-dashboard-placeholder" role="status">
                    아직 접수된 {cat.label} 의견이 없습니다.
                  </p>
                ) : (
                  <ul className="cp-admin-beta-feedback-list">
                    {cat.pageItems.map(({ row, text }) => (
                      <li key={`${cat.key}-${row.id}`} className="cp-admin-beta-feedback-item">
                        <div className="cp-admin-beta-feedback-item-top">
                          <div className="cp-admin-beta-feedback-item-meta">
                            <time dateTime={row.createdAt}>{formatAt(row.createdAt)}</time>
                            <span>
                              {row.fullName || '—'}
                              {row.email ? ` · ${row.email}` : ''}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="cp-admin-notices-icon-btn cp-admin-beta-feedback-delete-btn"
                            title="이 의견 삭제"
                            aria-label={`${cat.label} 의견 삭제`}
                            disabled={deleteBusy}
                            onClick={() => {
                              setDeleteError(null);
                              setDeleteTarget({
                                id: row.id,
                                category: cat.key,
                                fullName: row.fullName,
                                email: row.email,
                              });
                            }}
                          >
                            <img
                              src="/Trash.svg"
                              alt=""
                              className="cp-admin-notices-tool-icon"
                              width={15}
                              height={15}
                              decoding="async"
                            />
                          </button>
                        </div>
                        <div className="cp-admin-beta-feedback-item-text">{text}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <div className="cp-admin-beta-event-requests-wrap">
        <AdminBetaEventCodeRequestsPanel />
      </div>

      {deleteTarget ? (
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onClick={closeDeleteModal}
          onKeyDown={(e) => {
            if (e.key === 'Escape') closeDeleteModal();
          }}
        >
          <div
            className="cp-modal cp-admin-beta-delete-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-admin-beta-feedback-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-admin-beta-feedback-delete-title">의견 삭제</h3>
            <div className="cp-modal-body">
              <p>
                <strong>{FEEDBACK_CATEGORY_LABEL[deleteTarget.category]}</strong> 의견을
                삭제할까요?
              </p>
              <p className="cp-admin-beta-delete-confirm-meta">
                {deleteTarget.fullName || '—'}
                {deleteTarget.email ? ` · ${deleteTarget.email}` : ''}
              </p>
              {deleteError ? (
                <p className="cp-dashboard-placeholder" role="alert">
                  {deleteError}
                </p>
              ) : null}
            </div>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={closeDeleteModal}
                disabled={deleteBusy}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void confirmDeleteFeedback()}
                disabled={deleteBusy}
              >
                {deleteBusy ? '삭제 중…' : '삭제하기'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default AdminBetaTesterPage;
